// Holiday.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Holiday = () => {
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedHoliday, setSelectedHoliday] = useState(null);
    const [formData, setFormData] = useState({
        hname: '',
        hdate: '',
        htype: 'National'
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('date');
    const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
    const [monthFilter, setMonthFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');

    // User permissions state (similar to Employees.js)
    const [userPermissions, setUserPermissions] = useState({
        can_view: false,
        can_add: false,
        can_edit: false,
        can_delete: false,
        can_export: false,
        loaded: false
    });

    const navigate = useNavigate();
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    // Fetch user permissions function
    const fetchUserPermissions = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_export: false,
                    loaded: true
                });
                return;
            }

            const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && response.data.permissions) {
                // Find permissions for Holidays module
                const holidayPermissions = response.data.permissions.find(
                    p => p.module_name === 'Holidays'
                );

                if (holidayPermissions) {
                    setUserPermissions({
                        can_view: Boolean(holidayPermissions.can_view),
                        can_add: Boolean(holidayPermissions.can_add),
                        can_edit: Boolean(holidayPermissions.can_edit),
                        can_delete: Boolean(holidayPermissions.can_delete),
                        can_export: Boolean(holidayPermissions.can_export),
                        loaded: true
                    });
                } else {
                    // If no holiday permissions found, set all to false
                    setUserPermissions({
                        can_view: false,
                        can_add: false,
                        can_edit: false,
                        can_delete: false,
                        can_export: false,
                        loaded: true
                    });
                }
            } else {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_export: false,
                    loaded: true
                });
            }
        } catch (error) {
            console.error('Error fetching permissions:', error);
            setUserPermissions({
                can_view: false,
                can_add: false,
                can_edit: false,
                can_delete: false,
                can_export: false,
                loaded: true
            });
        }
    }, [API_BASE]);

    const fetchHolidays = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            if (!token) {
                setError('No authentication token found');
                setLoading(false);
                return;
            }

            const response = await axios.get(`${API_BASE}/get_holidays.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    year: yearFilter,
                    month: monthFilter,
                    type: typeFilter
                },
                timeout: 10000
            });

            if (response.data && response.data.success) {
                setHolidays(response.data.holidays || []);
                setError('');
            } else {
                setHolidays([]);
                const errorMsg = response.data?.error || 'Failed to fetch holidays';
                setError(errorMsg);
            }
        } catch (error) {
            setHolidays([]);
            const errorMsg = error.response?.data?.error ||
                error.message ||
                'Failed to fetch holidays';
            setError(errorMsg);
            console.error('Error fetching holidays:', error);
        } finally {
            setLoading(false);
        }
    }, [API_BASE, yearFilter, monthFilter, typeFilter]);

    useEffect(() => {
        const initializeData = async () => {
            await fetchUserPermissions();
            await fetchHolidays();
        };

        initializeData();
    }, [fetchUserPermissions, fetchHolidays]);

    // Permission check functions (similar to Employees.js)
    const canViewHolidays = () => userPermissions.can_view;
    const canAddHoliday = () => userPermissions.can_add;
    const canEditHoliday = () => userPermissions.can_edit;
    const canDeleteHoliday = () => userPermissions.can_delete;
    const canExportHolidays = () => userPermissions.can_export;

    const handleAdd = () => {
        if (!canAddHoliday()) {
            alert('You do not have permission to add holidays');
            return;
        }
        const today = new Date().toISOString().split('T')[0];
        setFormData({
            hname: '',
            hdate: today,
            htype: 'National'
        });
        setValidationErrors({});
        setShowAddModal(true);
    };

    const handleEdit = (holiday) => {
        if (!canEditHoliday()) {
            alert('You do not have permission to edit holidays');
            return;
        }
        setSelectedHoliday(holiday);
        setFormData({
            hname: holiday.hname,
            hdate: holiday.hdate,
            htype: holiday.htype
        });
        setValidationErrors({});
        setShowEditModal(true);
    };

    const handleDelete = (holiday) => {
        if (!canDeleteHoliday()) {
            alert('You do not have permission to delete holidays');
            return;
        }
        setSelectedHoliday(holiday);
        setShowDeleteModal(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.hname.trim()) {
            errors.hname = 'Holiday name is required';
        } else if (formData.hname.length > 100) {
            errors.hname = 'Holiday name cannot exceed 100 characters';
        }

        if (!formData.hdate) {
            errors.hdate = 'Holiday date is required';
        } else {
            const holidayDate = new Date(formData.hdate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (holidayDate < today) {
                errors.hdate = 'Holiday date cannot be in the past';
            }
        }

        if (!formData.htype) {
            errors.htype = 'Holiday type is required';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}/add_holiday.php`,
                formData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchHolidays();
                setShowAddModal(false);
                alert('Holiday added successfully');
            } else {
                setError(response.data.error || 'Failed to add holiday');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to add holiday');
            console.error('Error adding holiday:', error);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}/update_holiday.php`,
                {
                    holiday_id: selectedHoliday.holiday_id,
                    ...formData
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchHolidays();
                setShowEditModal(false);
                alert('Holiday updated successfully');
            } else {
                setError(response.data.error || 'Failed to update holiday');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to update holiday');
            console.error('Error updating holiday:', error);
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}/delete_holiday.php`,
                { holiday_id: selectedHoliday.holiday_id },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchHolidays();
                setShowDeleteModal(false);
                alert('Holiday deleted successfully');
            } else {
                setError(response.data.error || 'Failed to delete holiday');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to delete holiday');
            console.error('Error deleting holiday:', error);
        }
    };

    const getHolidayTypeBadge = (type) => {
        const badgeClasses = {
            'National': 'bg-danger',
            'State': 'bg-success',
            'Festival': 'bg-warning',
            'Local': 'bg-info'
        };
        return badgeClasses[type] || 'bg-secondary';
    };

    const filteredHolidays = holidays.filter(holiday => {
        return (
            holiday.hname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            holiday.htype?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    const sortedHolidays = [...filteredHolidays].sort((a, b) => {
        const dateA = new Date(a.hdate);
        const dateB = new Date(b.hdate);

        switch (sortBy) {
            case 'date':
                return dateA - dateB;
            case 'name':
                return a.hname.localeCompare(b.hname);
            case 'type':
                return a.htype.localeCompare(b.htype);
            default:
                return dateA - dateB;
        }
    });

    // Generate year options for filter
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear + i);

    // Generate month options for filter
    const monthOptions = [
        { value: '', label: 'All Months' },
        { value: '01', label: 'January' },
        { value: '02', label: 'February' },
        { value: '03', label: 'March' },
        { value: '04', label: 'April' },
        { value: '05', label: 'May' },
        { value: '06', label: 'June' },
        { value: '07', label: 'July' },
        { value: '08', label: 'August' },
        { value: '09', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
    ];

    // Generate type options for filter
    const typeOptions = [
        { value: '', label: 'All Types' },
        { value: 'National', label: 'National' },
        { value: 'State', label: 'State' },
        { value: 'Festival', label: 'Festival' },
        { value: 'Local', label: 'Local' }
    ];

    // Show loading while permissions are being fetched
    if (loading || !userPermissions.loaded) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading permissions...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // If user doesn't have view permission, show access denied
    if (!canViewHolidays()) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                                <h3 className="mt-3 text-warning">Access Denied</h3>
                                <p className="text-muted">You do not have permission to view holidays.</p>
                                <button
                                    className="btn btn-primary mt-3"
                                    onClick={() => navigate('/dashboard')}
                                >
                                    <i className="bi bi-speedometer2 me-2"></i>
                                    Back to Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <div className="btn-group me-3" role="group">
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/admin-panel')}
                                                title="Go to Dashboard"
                                            >
                                                <i className="bi bi-speedometer2"></i>
                                            </button>
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/Menu')}
                                                title="Go to Menu"
                                            >
                                                <i className="bi bi-menu-button"></i>
                                            </button>
                                        </div>
                                        <div>
                                            <h2 className="h4 mb-1 fw-bold text-dark">
                                                <i className="bi bi-calendar-event me-2 text-warning"></i>
                                                Holidays Management
                                                {!canAddHoliday() && !canEditHoliday() && !canDeleteHoliday() && (
                                                    <span className="badge bg-warning ms-2">View Only</span>
                                                )}
                                            </h2>
                                            <p className="text-muted mb-0">
                                                Manage company holidays for the year {yearFilter}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <div className="d-flex justify-content-end align-items-center">
                                        <button
                                            className="btn btn-outline-secondary me-2"
                                            onClick={() => navigate('/admin-panel')}
                                        >
                                            <i className="bi bi-arrow-left me-2"></i>
                                            Back to Dashboard
                                        </button>
                                        {canAddHoliday() && (
                                            <button
                                                className="btn btn-warning text-white"
                                                onClick={handleAdd}
                                            >
                                                <i className="bi bi-plus-circle me-2"></i>
                                                Add Holiday
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Navigation */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body py-3">
                            <div className="row text-center">
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/admin-panel')}
                                    >
                                        <i className="bi bi-speedometer2 me-2"></i>
                                        Dashboard
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/employees')}
                                    >
                                        <i className="bi bi-people me-2"></i>
                                        Employees
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/leave-types')}
                                    >
                                        <i className="bi bi-calendar2-week me-2"></i>
                                        Leave Types
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-warning text-white w-100 d-flex align-items-center justify-content-center"
                                        disabled
                                    >
                                        <i className="bi bi-calendar-event me-2"></i>
                                        Holidays
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/leave-master')}
                                    >
                                        <i className="bi bi-calendar-check me-2"></i>
                                        Leave Master
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-dark w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/Menu')}
                                    >
                                        <i className="bi bi-menu-button me-2"></i>
                                        Menu
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>
                                <strong>Error:</strong> {error}
                                <div className="mt-2">
                                    <button
                                        className="btn btn-sm btn-outline-danger me-2"
                                        onClick={fetchHolidays}
                                    >
                                        <i className="bi bi-arrow-clockwise me-1"></i>
                                        Retry
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setError('')}
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Search and Filter Bar */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row g-3 align-items-center">
                                <div className="col-md-3">
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="bi bi-search text-muted"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0"
                                            placeholder="Search holidays..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        {searchTerm && (
                                            <button
                                                className="btn btn-outline-secondary border-start-0"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={yearFilter}
                                        onChange={(e) => setYearFilter(e.target.value)}
                                    >
                                        {yearOptions.map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={monthFilter}
                                        onChange={(e) => setMonthFilter(e.target.value)}
                                    >
                                        {monthOptions.map(month => (
                                            <option key={month.value} value={month.value}>{month.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={typeFilter}
                                        onChange={(e) => setTypeFilter(e.target.value)}
                                    >
                                        {typeOptions.map(type => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-3 text-end">
                                    <div className="d-flex justify-content-end align-items-center">
                                        <button
                                            className="btn btn-outline-secondary me-2"
                                            onClick={fetchHolidays}
                                            title="Refresh"
                                        >
                                            <i className="bi bi-arrow-clockwise"></i>
                                        </button>
                                        {canExportHolidays() && (
                                            <button
                                                className="btn btn-outline-success me-2"
                                                onClick={() => alert('Export functionality coming soon!')}
                                                title="Export Holidays"
                                            >
                                                <i className="bi bi-download"></i>
                                            </button>
                                        )}
                                        {canAddHoliday() && (
                                            <button
                                                className="btn btn-warning text-white"
                                                onClick={handleAdd}
                                            >
                                                <i className="bi bi-plus-circle me-2"></i>
                                                Add Holiday
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Holidays Calendar Preview */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">
                                    <i className="bi bi-calendar3 me-2"></i>
                                    Holidays Calendar {yearFilter}
                                </h5>
                                <select
                                    className="form-select w-auto"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                >
                                    <option value="date">Sort by Date</option>
                                    <option value="name">Sort by Name</option>
                                    <option value="type">Sort by Type</option>
                                </select>
                            </div>

                            {sortedHolidays.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-calendar-event display-1 text-muted"></i>
                                    <h4 className="mt-3 text-muted">No holidays found</h4>
                                    <p className="text-muted">
                                        {holidays.length === 0
                                            ? "No holidays scheduled. Add your first holiday."
                                            : "No holidays match your search criteria."
                                        }
                                    </p>
                                    {holidays.length === 0 && canAddHoliday() && (
                                        <button
                                            className="btn btn-warning text-white mt-2"
                                            onClick={handleAdd}
                                        >
                                            <i className="bi bi-plus-circle me-2"></i>
                                            Add First Holiday
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="ps-4">Date</th>
                                                <th>Holiday Name</th>
                                                <th>Type</th>
                                                <th>Day</th>
                                                <th className="text-end pe-4">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedHolidays.map((holiday) => {
                                                const holidayDate = new Date(holiday.hdate);
                                                const dayName = holidayDate.toLocaleDateString('en-US', { weekday: 'long' });
                                                const formattedDate = holidayDate.toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                });

                                                const isPast = holidayDate < new Date();

                                                return (
                                                    <tr
                                                        key={holiday.holiday_id}
                                                        className={isPast ? 'text-muted' : ''}
                                                    >
                                                        <td className="ps-4">
                                                            <div className="d-flex align-items-center">
                                                                <div className="date-icon bg-warning text-white rounded d-flex flex-column align-items-center justify-content-center me-3"
                                                                    style={{ width: '60px', height: '60px' }}
                                                                >
                                                                    <div className="fs-6 fw-bold">
                                                                        {holidayDate.getDate()}
                                                                    </div>
                                                                    <div className="fs-8">
                                                                        {holidayDate.toLocaleDateString('en-US', { month: 'short' })}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <div className="fw-semibold">
                                                                        {formattedDate}
                                                                    </div>
                                                                    <small className="text-muted">
                                                                        ID: {holiday.holiday_id}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="fw-semibold">{holiday.hname}</div>
                                                            {isPast && (
                                                                <small className="text-muted">
                                                                    <i className="bi bi-clock-history me-1"></i>
                                                                    Past holiday
                                                                </small>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${getHolidayTypeBadge(holiday.htype)}`}>
                                                                {holiday.htype}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${isPast ? 'bg-secondary' : 'bg-primary'}`}>
                                                                {dayName}
                                                            </span>
                                                        </td>
                                                        <td className="text-end pe-4">
                                                            <div className="btn-group" role="group">
                                                                {canEditHoliday() && (
                                                                    <button
                                                                        className="btn btn-outline-primary btn-sm"
                                                                        onClick={() => handleEdit(holiday)}
                                                                        title="Edit Holiday"
                                                                    >
                                                                        <i className="bi bi-pencil"></i>
                                                                    </button>
                                                                )}
                                                                {canDeleteHoliday() && (
                                                                    <button
                                                                        className="btn btn-outline-danger btn-sm"
                                                                        onClick={() => handleDelete(holiday)}
                                                                        title="Delete Holiday"
                                                                    >
                                                                        <i className="bi bi-trash"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Footer */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body py-3">
                            <div className="row text-center">
                                <div className="col-md-3">
                                    <div className="fw-bold text-warning fs-4">{holidays.length}</div>
                                    <small className="text-muted">Total Holidays</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-danger fs-4">
                                        {holidays.filter(h => h.htype === 'National').length}
                                    </div>
                                    <small className="text-muted">National Holidays</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-success fs-4">
                                        {holidays.filter(h => h.htype === 'State').length}
                                    </div>
                                    <small className="text-muted">State Holidays</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-info fs-4">
                                        {holidays.filter(h => h.htype === 'Festival').length}
                                    </div>
                                    <small className="text-muted">Festival Holidays</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions Footer */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card border-0">
                        <div className="card-body text-center py-3">
                            <div className="btn-group" role="group">
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={fetchHolidays}
                                >
                                    <i className="bi bi-arrow-clockwise me-2"></i>
                                    Refresh Data
                                </button>
                                <button
                                    className="btn btn-outline-success"
                                    onClick={() => navigate('/employees')}
                                >
                                    <i className="bi bi-people me-2"></i>
                                    Manage Employees
                                </button>
                                <button
                                    className="btn btn-outline-info"
                                    onClick={() => navigate('/leave-types')}
                                >
                                    <i className="bi bi-calendar2-week me-2"></i>
                                    Leave Types
                                </button>
                                {canAddHoliday() && (
                                    <button
                                        className="btn btn-warning text-white"
                                        onClick={handleAdd}
                                    >
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add New Holiday
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals remain the same */}
            {/* Add Holiday Modal */}
            {showAddModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header" style={{
                                background: 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)',
                                borderBottom: 'none'
                            }}>
                                <h5 className="modal-title text-white">
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Add New Holiday
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowAddModal(false)}
                                ></button>
                            </div>

                            <form onSubmit={handleAddSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label htmlFor="hname" className="form-label fw-medium">
                                            Holiday Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${validationErrors.hname ? 'is-invalid' : ''}`}
                                            id="hname"
                                            name="hname"
                                            value={formData.hname}
                                            onChange={handleChange}
                                            placeholder="e.g., New Year's Day, Diwali, Christmas"
                                            required
                                        />
                                        {validationErrors.hname && (
                                            <div className="invalid-feedback">{validationErrors.hname}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="hdate" className="form-label fw-medium">
                                            Holiday Date <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            className={`form-control ${validationErrors.hdate ? 'is-invalid' : ''}`}
                                            id="hdate"
                                            name="hdate"
                                            value={formData.hdate}
                                            onChange={handleChange}
                                            min={new Date().toISOString().split('T')[0]}
                                            required
                                        />
                                        {validationErrors.hdate && (
                                            <div className="invalid-feedback">{validationErrors.hdate}</div>
                                        )}
                                        <div className="form-text">
                                            {formData.hdate && new Date(formData.hdate).toLocaleDateString('en-US', {
                                                weekday: 'long',
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="htype" className="form-label fw-medium">
                                            Holiday Type <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-select ${validationErrors.htype ? 'is-invalid' : ''}`}
                                            id="htype"
                                            name="htype"
                                            value={formData.htype}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="National">National Holiday</option>
                                            <option value="State">State Holiday</option>
                                            <option value="Festival">Festival</option>
                                            <option value="Local">Local Holiday</option>
                                        </select>
                                        {validationErrors.htype && (
                                            <div className="invalid-feedback">{validationErrors.htype}</div>
                                        )}
                                        <div className="form-text">
                                            Select the type of holiday for categorization
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-light border-secondary-subtle"
                                        onClick={() => setShowAddModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-warning text-white"
                                    >
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add Holiday
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Holiday Modal */}
            {showEditModal && selectedHoliday && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header" style={{
                                background: 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)',
                                borderBottom: 'none'
                            }}>
                                <h5 className="modal-title text-white">
                                    <i className="bi bi-pencil-square me-2"></i>
                                    Edit Holiday
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowEditModal(false)}
                                ></button>
                            </div>

                            <form onSubmit={handleEditSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label htmlFor="hname" className="form-label fw-medium">
                                            Holiday Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${validationErrors.hname ? 'is-invalid' : ''}`}
                                            id="hname"
                                            name="hname"
                                            value={formData.hname}
                                            onChange={handleChange}
                                            required
                                        />
                                        {validationErrors.hname && (
                                            <div className="invalid-feedback">{validationErrors.hname}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="hdate" className="form-label fw-medium">
                                            Holiday Date <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            className={`form-control ${validationErrors.hdate ? 'is-invalid' : ''}`}
                                            id="hdate"
                                            name="hdate"
                                            value={formData.hdate}
                                            onChange={handleChange}
                                            required
                                        />
                                        {validationErrors.hdate && (
                                            <div className="invalid-feedback">{validationErrors.hdate}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="htype" className="form-label fw-medium">
                                            Holiday Type <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-select ${validationErrors.htype ? 'is-invalid' : ''}`}
                                            id="htype"
                                            name="htype"
                                            value={formData.htype}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="National">National Holiday</option>
                                            <option value="State">State Holiday</option>
                                            <option value="Festival">Festival</option>
                                            <option value="Local">Local Holiday</option>
                                        </select>
                                        {validationErrors.htype && (
                                            <div className="invalid-feedback">{validationErrors.htype}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-light border-secondary-subtle"
                                        onClick={() => setShowEditModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-warning text-white"
                                    >
                                        <i className="bi bi-check-circle me-2"></i>
                                        Update Holiday
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedHoliday && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-danger text-white">
                                <h5 className="modal-title">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    Confirm Delete
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowDeleteModal(false)}
                                ></button>
                            </div>

                            <div className="modal-body">
                                <div className="alert alert-warning" role="alert">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    <strong>Warning:</strong> This action cannot be undone.
                                </div>

                                <p>Are you sure you want to delete the following holiday?</p>

                                <div className="card border-0 bg-light">
                                    <div className="card-body">
                                        <h6 className="fw-bold">{selectedHoliday.hname}</h6>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <small className="text-muted d-block">Date</small>
                                                <strong>
                                                    {new Date(selectedHoliday.hdate).toLocaleDateString('en-US', {
                                                        weekday: 'long',
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </strong>
                                            </div>
                                            <div className="col-md-6">
                                                <small className="text-muted d-block">Type</small>
                                                <span className={`badge ${getHolidayTypeBadge(selectedHoliday.htype)}`}>
                                                    {selectedHoliday.htype}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="alert alert-danger mt-3" role="alert">
                                    <i className="bi bi-info-circle me-2"></i>
                                    <strong>Note:</strong> Deleting this holiday may affect employee leave calculations.
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light border-secondary-subtle"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleDeleteConfirm}
                                >
                                    <i className="bi bi-trash me-2"></i>
                                    Delete Holiday
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Holiday;